import type React from 'react'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AppBillingBanner } from '@/components/billing/AppBillingBanner'
import Footer from '@/components/Footer'
import GhostTopBar from '@/components/GhostTopBar'
import Navbar from '@/components/Navbar'

// The path focus was last moved for. MODULE scope, not a component ref: PageLayout remounts on
// most navigations (each page renders its own), so a ref would reset every time and every route
// would look like the first. It also makes the effect idempotent under StrictMode's double-invoke,
// which would otherwise move focus on the very first load.
let lastFocusedPath: string | null = null

/**
 * A layout rendering its OWN nav inside `main` marks the element route focus should land on, so Tab
 * doesn't walk back through the nav. Mark the content region, not the nav. Only one at a time.
 */
export const ROUTE_FOCUS_SELECTOR = '[data-route-focus]'

interface PageLayoutProps {
  children: React.ReactNode
  // Drop the desktop top padding so a full-bleed image can meet the nav.
  flushTop?: boolean
  // For a page that draws its own chrome over a photo: no mobile top bar, and no padding on mobile or from lg up.
  immersive?: boolean
  // Desktop nav bar see-through over the page's photo (lg+), for as long as the photo is under it.
  navOverlay?: boolean
}

export default function PageLayout({ children, flushTop = false, immersive = false, navOverlay = false }: PageLayoutProps) {
  const { pathname } = useLocation()
  const mainRef = useRef<HTMLElement>(null)

  // A route change leaves focus on <body>, so Tab restarts at the top and nothing announces the new
  // page. Keyed on `pathname`, not mount: nested routes swap through an Outlet without remounting.
  useEffect(() => {
    if (lastFocusedPath === pathname) return
    const isFirstRoute = lastFocusedPath === null
    lastFocusedPath = pathname
    if (isFirstRoute) return
    const main = mainRef.current
    if (!main) return
    // preventScroll: ScrollToTop owns scroll position. The target sits below the header, so
    // focusing it would scroll that header off-screen and fight the scroll-to-top that just ran.
    const target = main.querySelector<HTMLElement>(ROUTE_FOCUS_SELECTOR) ?? main
    target.focus({ preventScroll: true })
  }, [pathname])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navbar (md+) is a full-width sticky blur bar and self-gates via `hidden md:flex`. */}
      <Navbar overlay={navOverlay} />
      {!immersive && <GhostTopBar />}
      {/* The app's only `main` landmark (the login/invite shells have their own, and don't use
          this layout). `tabIndex={-1}` makes it a programmatic focus target without adding a tab
          stop; the outline is suppressed because focus arrives here by navigation, not by Tab —
          a ring around the whole page would be noise, and the announcement is the real feedback. */}
      <main
        ref={mainRef}
        tabIndex={-1}
        className={`flex-1 flex flex-col bg-dot-grid pb-nav-safe md:pb-8 ${immersive ? 'md:px-4 lg:px-0' : 'px-4 pt-topbar-safe lg:px-16 xl:px-24 2xl:px-40'} ${flushTop ? 'md:pt-0' : 'md:pt-5'} ${immersive ? 'lg:py-0' : ''} focus:outline-none`}>
        {/* Mounted here rather than above `main` so it clears the fixed mobile top bar, which
            occupies no flow height — `pt-topbar-safe` is the only thing holding content below it.
            Inside `main` it also lands in the same slot pages already give `AppConnectionBanner`,
            which is what makes the two suppressible against each other. */}
        <AppBillingBanner />
        {children}
      </main>
      <div className={`hidden md:block mt-10 ${immersive ? 'lg:mt-0' : ''}`}>
        <Footer />
      </div>
    </div>
  )
}
